/**
 * design-tools/useGSAPReveal.js
 * Scroll-triggered reveal animations using IntersectionObserver
 * Mimics GSAP ScrollTrigger behavior without the bundle cost
 *
 * Tuned for a snappier reveal: lower stagger delay, larger negative
 * rootMargin so elements trigger a bit earlier (before fully entering
 * the viewport), and requestAnimationFrame batching instead of
 * setTimeout for the no-stagger case so the browser can coalesce
 * the class toggle with the next paint instead of an arbitrary timer tick.
 */
import { useEffect } from 'react';

export function useGSAPReveal(selector = '.gsap-reveal', options = {}) {
  useEffect(() => {
    const {
      threshold = 0.1,
      rootMargin = '0px 0px -40px 0px',
      staggerDelay = 40,
    } = options;

    const els = document.querySelectorAll(selector);
    if (!els.length) return;

    let counter = 0;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const delay = parseFloat(el.dataset.revealDelay || 0) + counter * staggerDelay;
            if (delay <= 0) {
              // No delay needed — flip the class on the next frame instead
              // of queuing a timer, so it lands as soon as the browser
              // is ready to paint rather than waiting on timer granularity.
              requestAnimationFrame(() => el.classList.add('gsap-revealed'));
            } else {
              setTimeout(() => {
                el.classList.add('gsap-revealed');
              }, delay);
            }
            counter++;
            io.unobserve(el);
          }
        });
      },
      { threshold, rootMargin },
    );

    els.forEach((el) => io.observe(el));

    return () => io.disconnect();
  }, [selector]);
}

/**
 * useStaggerReveal
 * Reveals children of a container with staggered delays
 */
export function useStaggerReveal(containerSelector = '.stagger-container') {
  useEffect(() => {
    const containers = document.querySelectorAll(containerSelector);
    containers.forEach((container) => {
      const children = container.querySelectorAll('.stagger-child');
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              children.forEach((child, i) => {
                setTimeout(() => child.classList.add('stagger-visible'), i * 55);
              });
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 },
      );
      io.observe(container);
    });
  }, [containerSelector]);
}
